/**
 * Normalizes a path by extracting the base group ID.
 * Handles tree-prefixed IDs like "default.~live|H6AgxnYWsnKnmP!rVpf3KjzSNxJIT"
 * and converts them to "default.rVpf3KjzSNxJIT".
 *
 * This ensures draft and live versions of the same task have matching simplified IDs.
 *
 * @param {string} path - the path to normalize (e.g., "default.~live|H6AgxnYWsnKnmP!rVpf3KjzSNxJIT")
 * @returns {string} the normalized path (e.g., "default.rVpf3KjzSNxJIT")
 */
function normalizePath(path) {
    // Path format: "{prefix}.{groupElementId}" where groupElementId might have tree prefix
    // e.g., "default.~live|H6AgxnYWsnKnmP!rVpf3KjzSNxJIT" -> "default.rVpf3KjzSNxJIT"
    // e.g., "default.rVpf3KjzSNxJIT" -> "default.rVpf3KjzSNxJIT" (unchanged)

    const firstDotIndex = path.indexOf(".")
    if (firstDotIndex === -1) {
        return path
    }

    const prefix = path.slice(0, firstDotIndex)
    const groupPart = path.slice(firstDotIndex + 1)

    // If groupPart contains "!", extract the base group ID (last part after "!")
    // e.g., "~live|H6AgxnYWsnKnmP!rVpf3KjzSNxJIT" -> "rVpf3KjzSNxJIT"
    if (groupPart.includes("!")) {
        const baseGroupId = groupPart.split("!").at(-1)
        return `${prefix}.${baseGroupId}`
    }

    return path
}

/**
 * Create a simplified task id that removes the period and asset index
 * @param {string} taskId - the id to simplify
 * @returns {string} the simplified id
 */
export function simplifyTaskId(taskId) {
    // Split on the LAST "!" to handle IDs with multiple "!" in the path
    // e.g., "default.~regime123!group456!789.1.0.0" should split into:
    //   path = "default.~regime123!group456"
    //   code = "789.1.0.0"
    const lastBangIndex = taskId.lastIndexOf("!")
    if (lastBangIndex === -1) {
        // No "!" found, return as-is
        return taskId
    }
    const path = taskId.slice(0, lastBangIndex)
    const code = taskId.slice(lastBangIndex + 1)
    const codes = code.split(".")

    // Normalize the path to extract base group ID, ensuring draft and live IDs match
    const normalizedPath = normalizePath(path)

    // Keep the scheduleId as-is, retaining any GT prefix
    const scheduleId = codes[0]

    switch (codes.length) {
        case 2:
            // This is a new id without details (scheduleId.taskId)
            return `${normalizedPath}!${scheduleId}.${codes[1]}`

        case 3:
            // This is an old id without details (scheduleId.taskId.extra)
            return `${normalizedPath}!${scheduleId}.${codes.slice(1).join(".")}`

        case 4:
            // This is a new id with details (scheduleId.taskId.assetIndex.periodIndex)
            return `${normalizedPath}!${scheduleId}.${codes[1]}`

        default:
            // Old id with details or other format
            return `${normalizedPath}!${scheduleId}.${codes.slice(1, 3).join(".")}`
    }
}
